import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, User } from 'lucide-react';
import { publicProspectPageConfig } from '../../config/publicProspectPageConfig';

export default function PublicProspectForm({
  intent,
  language,
  copy,
  form,
  errors,
  message,
  status,
  disabled,
  onChange,
  onSubmit,
  onBack,
  onRefresh,
  errorSummaryRef,
}) {
  const formCopy =
    publicProspectPageConfig.forms[intent]?.[language] ||
    publicProspectPageConfig.forms[intent]?.fr ||
    {};
  const sale = intent === 'property_sale';
  const fieldError = (key) =>
    errors[key] ? (
      <p id={`${key}-error`} className="mt-1 text-sm font-medium text-red-700">
        {copy.errors[errors[key]] || copy.errors.required}
      </p>
    ) : null;
  const inputClass =
    'prospect-ui mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 text-base text-[#1f4a52] focus:border-[#004747] focus:outline-none focus:ring-2 focus:ring-[#007c91] disabled:bg-gray-100 disabled:opacity-70';

  return (
    <form noValidate onSubmit={onSubmit} className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={disabled}
          className="prospect-ui rounded px-2 py-1 text-sm font-medium text-[#004747] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91] disabled:opacity-60"
        >
          {copy.back}
        </button>
      </div>
      <div>
        <h2 className="prospect-display text-[2rem] font-semibold leading-[1.08] tracking-tight text-[#0B1F2A]">
          {formCopy.heading}
        </h2>
        <p className="prospect-ui mt-2 text-[1.0625rem] leading-relaxed text-[#1f4a52]">{formCopy.support}</p>
      </div>
      {message && status === 'retry' ? (
        <div ref={errorSummaryRef} tabIndex={-1} role="alert" className="rounded-lg border border-amber-500 bg-amber-50 p-3 text-amber-950">
          {message}
        </div>
      ) : null}
      {Object.keys(errors).length ? (
        <div ref={errorSummaryRef} tabIndex={-1} role="alert" className="rounded-lg border border-red-500 bg-red-50 p-3 text-red-900">
          {copy.errorSummary}
        </div>
      ) : null}
      <label className="block">
        <span className="font-medium text-[#004747]">{copy.fullName}</span>
        <span className="relative mt-1 block">
          <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            className={inputClass}
            autoComplete="name"
            value={form.contact.fullName}
            disabled={disabled}
            aria-invalid={Boolean(errors.fullName)}
            aria-describedby={errors.fullName ? 'fullName-error' : undefined}
            onChange={(event) => onChange(['contact', 'fullName'], event.target.value)}
          />
        </span>
        {fieldError('fullName')}
      </label>
      <label className="block">
        <span className="font-medium text-[#004747]">{copy.email}</span>
        <span className="relative mt-1 block">
          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            className={inputClass}
            type="email"
            autoComplete="email"
            value={form.contact.email}
            disabled={disabled}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'email-error' : undefined}
            onChange={(event) => onChange(['contact', 'email'], event.target.value)}
          />
        </span>
        {fieldError('email')}
      </label>
      {sale ? (
        <label className="block">
          <span className="font-medium text-[#004747]">{copy.phone}</span>
          <span className="relative mt-1 block">
            <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input
              className={inputClass}
              type="tel"
              autoComplete="tel"
              value={form.contact.phone}
              disabled={disabled}
              aria-invalid={Boolean(errors.phone)}
              aria-describedby={errors.phone ? 'phone-error' : undefined}
              onChange={(event) => onChange(['contact', 'phone'], event.target.value)}
            />
          </span>
          {fieldError('phone')}
        </label>
      ) : null}
      {sale ? (
        <label className="flex items-start gap-3 rounded-xl border border-gray-300 p-4" htmlFor="prospect-ownership">
          <input
            id="prospect-ownership"
            className="mt-1"
            type="checkbox"
            checked={form.ownershipAttested === true}
            disabled={disabled}
            aria-invalid={Boolean(errors.ownership)}
            aria-describedby={errors.ownership ? 'ownership-error' : undefined}
            onChange={(event) => onChange(['ownershipAttested'], event.target.checked)}
          />
          <span>{copy.ownershipAttestation}</span>
        </label>
      ) : null}
      {sale && fieldError('ownership')}
      <label className="flex items-start gap-3 rounded-xl border border-gray-300 p-4" htmlFor="prospect-consent">
        <input
          id="prospect-consent"
          className="mt-1"
          type="checkbox"
          checked={form.consentAccepted === true}
          disabled={disabled}
          aria-invalid={Boolean(errors.consent)}
          aria-describedby={errors.consent ? 'consent-error' : undefined}
          onChange={(event) => onChange(['consentAccepted'], event.target.checked)}
        />
        <span>{copy.consent}</span>
      </label>
      {fieldError('consent')}
      {disabled ? (
        <span className="inline-block text-gray-500">{copy.privacy}</span>
      ) : (
        <Link
          className="inline-block font-medium text-[#005f73] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]"
          to="/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          {copy.privacy}
        </Link>
      )}
      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          disabled={disabled}
          type="submit"
          className="prospect-ui inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-xl bg-[#004747] px-5 py-3 text-[1.0625rem] font-bold text-white hover:bg-[#003636] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91] focus-visible:ring-offset-2 disabled:opacity-60"
        >
          {disabled ? copy.submitting : status === 'retry' ? copy.retry : formCopy.cta}
          <span aria-hidden="true">→</span>
        </button>
        {status === 'retry' ? (
          <button
            type="button"
            disabled={disabled}
            className="prospect-ui rounded-xl border border-[#004747] px-5 py-3 text-[1.0625rem] font-bold text-[#004747] disabled:opacity-60"
            onClick={onRefresh}
          >
            {copy.refresh}
          </button>
        ) : null}
      </div>
    </form>
  );
}
